import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { tagIdsOf } from '../epmHelpers';
import Badge from '../../UI/Badge';
import {
    OUTCOMES,
    IN_FLIGHT,
    OUTCOME_STYLES,
    getVerdictOutcome,
} from './utils';

export const TagBreakdown = ({ tests = [], tagsMap = {} }) => {
    const navigate = useNavigate();

    const rows = useMemo(() => {
        const tagMapValues = Object.values(tagsMap || {});
        return tagMapValues
            .map((tag) => {
                const id = String(tag._id);
                const taggedTests = tests.filter((test) => tagIdsOf(test).includes(id));
                const concludedTests = taggedTests.filter((test) => test.status === 'CONCLUDED');
                const inFlight = taggedTests.filter((test) => IN_FLIGHT.includes(test.status)).length;
                const concluded = concludedTests.length;

                const history = { win: 0, loss: 0, inconclusive: 0, invalid: 0 };
                concludedTests.forEach((test) => {
                    const outcome = getVerdictOutcome(test);
                    if (outcome) {
                        history[outcome] += 1;
                    }
                });

                const winRate = concluded ? Math.round((history.win / concluded) * 100) : 0;

                return {
                    id,
                    name: tag.name,
                    history,
                    concluded,
                    inFlight,
                    winRate,
                };
            })
            .filter((row) => row.concluded > 0 || row.inFlight > 0)
            .sort((a, b) => b.concluded - a.concluded || b.inFlight - a.inFlight);
    }, [tests, tagsMap]);

    if (!rows.length) {
        return (
            <p className="text-xs text-gray-400 mt-4 py-1 italic">
                No tagged test activity in this period.
            </p>
        );
    }

    return (
        <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-xs">
                <thead>
                    <tr className="border-b border-gray-100">
                        <th className="py-2.5 pr-3 pl-0 text-gray-400 text-[11px] font-bold tracking-wider text-left uppercase">Tag</th>
                        <th className="py-2.5 px-3 text-gray-400 text-[11px] font-bold tracking-wider text-left uppercase">Concluded</th>
                        <th className="py-2.5 px-3 text-gray-400 text-[11px] font-bold tracking-wider text-left uppercase">Win rate</th>
                        <th className="w-[32%] py-2.5 px-3 text-gray-400 text-[11px] font-bold tracking-wider text-left uppercase">Outcomes</th>
                        <th className="py-2.5 px-3 text-gray-400 text-[11px] font-bold tracking-wider text-left uppercase">In flight</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                    {rows.map((row) => (
                        <tr
                            key={row.id}
                            onClick={() => navigate(`/epm/testing-board?tag=${row.id}`)}
                            title={`Open the board filtered to ${row.name}`}
                            className="cursor-pointer transition-colors duration-100 hover:bg-slate-50/80"
                        >
                            <td className="py-3 pr-3 pl-0 text-gray-800">
                                <Badge variant="primary" appearance="soft" size="sm">
                                    {row.name}
                                </Badge>
                            </td>
                            <td className="py-3 px-3 text-gray-700 font-medium">{row.concluded}</td>
                            <td className="py-3 px-3 text-gray-700">
                                <strong className="text-primary font-bold">{row.winRate}%</strong>
                            </td>
                            <td className="w-[32%] py-3 px-3">
                                <span
                                    className="flex h-2.5 overflow-hidden rounded-full bg-slate-100"
                                    aria-label={OUTCOMES.map((o) => `${o} ${row.history[o]}`).join(', ')}
                                    style={row.concluded === 0 ? { background: '#edf0f7' } : undefined}
                                >
                                    {OUTCOMES.map((outcome) => row.history[outcome] > 0 && (
                                        <span
                                            key={outcome}
                                            className={`block h-full ${OUTCOME_STYLES[outcome]}`}
                                            style={{ width: `${(row.history[outcome] / row.concluded) * 100}%` }}
                                        />
                                    ))}
                                </span>
                            </td>
                            <td className="py-3 px-3 text-gray-500">{row.inFlight}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default TagBreakdown;
